import { useEffect, useState } from "react";
import { CloudOff, RefreshCw, Wifi, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { flushQueue, getPendingCount, subscribeQueue } from "@/lib/offlineQueue";

export default function OfflineIndicator() {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(getPendingCount());
  const [syncing, setSyncing] = useState(false);

  const doFlush = async ({ silent } = {}) => {
    if (!navigator.onLine) return;
    if (getPendingCount() === 0) return;
    setSyncing(true);
    const { synced, failed } = await flushQueue();
    setSyncing(false);
    setPending(getPendingCount());
    if (!silent && synced > 0) toast.success(`Synced ${synced} refuel${synced === 1 ? "" : "s"}`);
    if (failed > 0 && synced === 0 && !silent) toast.error("Sync failed — retrying later");
  };

  useEffect(() => {
    const onOnline = () => {
      setOnline(true);
      doFlush({ silent: false });
    };
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    const unsub = subscribeQueue((c) => setPending(c));
    // attempt an initial flush if we come in with a queue + net
    if (navigator.onLine && getPendingCount() > 0) doFlush({ silent: true });
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      unsub();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (online && pending === 0) return null;

  if (!online) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B] text-xs font-bold uppercase tracking-wider" data-testid="offline-badge">
        <CloudOff size={14} />
        <span className="hidden sm:inline">Offline</span>
        {pending > 0 && <span className="font-mono">· {pending} queued</span>}
      </div>
    );
  }

  // online with pending
  return (
    <button
      onClick={() => doFlush({ silent: false })}
      className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 text-[#FF5A1F] text-xs font-bold uppercase tracking-wider hover:bg-[#FF5A1F]/20 transition-colors duration-200"
      data-testid="sync-pending-badge"
    >
      {syncing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
      <span className="font-mono">{pending} to sync</span>
    </button>
  );
}
